import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BookOpen, ArrowLeft, Pencil, CheckCircle2, Archive, Copy, Trash2, AlertTriangle,
  ExternalLink, ArrowRight, Link2, ShieldCheck, Undo2, Video, Image as ImageIcon,
} from "lucide-react";
import { C, StatusBadge, AudienceChips, fmtDate } from "./sopsUi";

export default function SopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sop, setSop] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/sops/${id}`).then(({ data }) => setSop(data)).catch((e) => { toast.error(errMsg(e)); navigate("/sops"); });
  useEffect(() => { setSop(null); load(); /* eslint-disable-next-line */ }, [id]);

  const act = async (verb, method = "post") => {
    setBusy(true);
    try {
      await api[method](`/sops/${id}/${verb}`);
      toast.success("Done.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this SOP? This cannot be undone.")) return;
    setBusy(true);
    try { await api.delete(`/sops/${id}`); toast.success("Deleted."); navigate("/sops"); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const duplicate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/sops/${id}/duplicate`); toast.success("Duplicated."); navigate(`/sops/${data.id}/edit`); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (!sop) return <div className="max-w-3xl mx-auto space-y-3"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-40" /></div>;
  const canManage = sop.can_manage;

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="sop-detail-page">
      <button onClick={() => navigate("/sops")} className="text-[13px] flex items-center gap-1 mb-3" style={{ color: C.slate }}>
        <ArrowLeft size={14} /> Back to SOP Library
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }} data-testid="sop-title">
            <BookOpen size={20} style={{ color: C.navy }} /> {sop.title}
          </h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <StatusBadge status={sop.status} />
            <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(28,67,88,0.08)", color: C.navy }}>{sop.category_label}</span>
            <span className="text-[11.5px]" style={{ color: C.slate }}>v{sop.version} · updated {fmtDate(sop.updated_at)}</span>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => navigate(`/sops/${id}/edit`)} data-testid="sop-edit-btn" className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Pencil size={13} /> Edit</Button>
            {sop.status !== "published" && <Button size="sm" disabled={busy} onClick={() => act("publish")} data-testid="sop-publish-btn" className="h-8 gap-1 bg-[#2F7D5A] hover:bg-[#276849] text-white"><CheckCircle2 size={13} /> Publish</Button>}
            {sop.status === "published" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("unpublish")} data-testid="sop-unpublish-btn" className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.amber }}><Undo2 size={13} /> Unpublish</Button>}
            <Button size="sm" variant="outline" disabled={busy} onClick={duplicate} data-testid="sop-duplicate-btn" className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Copy size={13} /> Duplicate</Button>
            {sop.status !== "archived" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("archive")} data-testid="sop-archive-btn" className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.slate }}><Archive size={13} /> Archive</Button>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={del} data-testid="sop-delete-btn" className="h-8 gap-1 text-[#9C1E22]"><Trash2 size={13} /> Delete</Button>
          </div>
        )}
      </div>

      {sop.summary && <p className="text-[15px] mt-4" style={{ color: C.charcoal }}>{sop.summary}</p>}

      {sop.role_requirements && (
        <div className="mt-4 rounded-lg p-3 flex items-start gap-2" style={{ background: "rgba(28,67,88,0.05)" }} data-testid="sop-role-req">
          <ShieldCheck size={16} style={{ color: C.navy }} className="mt-0.5 shrink-0" />
          <div><div className="text-[12px] font-semibold" style={{ color: C.navy }}>Who can do this</div>
            <div className="text-[13px]" style={{ color: C.charcoal }}>{sop.role_requirements}</div></div>
        </div>
      )}

      {(sop.steps || []).length > 0 && (
        <Card className="p-5 border-[#E6EDF2] mt-4" data-testid="sop-steps">
          <div className="text-[12px] font-semibold uppercase tracking-wide mb-3" style={{ color: `${C.navy}B3` }}>Steps</div>
          <ol className="space-y-3">
            {sop.steps.map((st, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold text-white" style={{ background: C.navy }}>{i + 1}</span>
                <div>
                  {st.title && <div className="text-[14px] font-semibold" style={{ color: C.charcoal }}>{st.title}</div>}
                  {st.detail && <div className="text-[13.5px]" style={{ color: C.slate }}>{st.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {(sop.warnings || []).length > 0 && (
        <div className="mt-4 rounded-lg p-3 border" style={{ background: "#FEF3C7", borderColor: "#FDE68A" }} data-testid="sop-warnings">
          <div className="text-[12px] font-semibold flex items-center gap-1.5 mb-1" style={{ color: "#92400E" }}><AlertTriangle size={14} /> Guardrails</div>
          <ul className="list-disc pl-5 space-y-1">
            {sop.warnings.map((w, i) => <li key={i} className="text-[13px]" style={{ color: "#78350F" }}>{w}</li>)}
          </ul>
        </div>
      )}

      {(sop.related_routes || []).length > 0 && (
        <div className="mt-4" data-testid="sop-related-routes">
          <div className="text-[12px] font-semibold uppercase tracking-wide mb-2" style={{ color: `${C.navy}B3` }}>Where to go</div>
          <div className="flex flex-wrap gap-2">
            {sop.related_routes.map((r) => (
              <Link key={r} to={r} className="text-[12.5px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1"
                style={{ borderColor: C.line, color: C.navy }}><ExternalLink size={12} /> {r}</Link>
            ))}
          </div>
        </div>
      )}

      {(sop.links || []).length > 0 && (
        <div className="mt-4" data-testid="sop-links">
          <div className="text-[12px] font-semibold uppercase tracking-wide mb-2" style={{ color: `${C.navy}B3` }}>Links</div>
          <div className="flex flex-col gap-1">
            {sop.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-[13px] inline-flex items-center gap-1" style={{ color: C.sky }}>
                <Link2 size={13} /> {l.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {(sop.screenshots || []).length > 0 && (
        <div className="mt-4" data-testid="sop-screenshots">
          <div className="text-[12px] font-semibold uppercase tracking-wide mb-2 flex items-center gap-1" style={{ color: `${C.navy}B3` }}><ImageIcon size={13} /> Screenshots</div>
          <div className="grid grid-cols-2 gap-2">
            {sop.screenshots.map((s, i) => <img key={i} src={s} alt="" className="rounded-lg border w-full object-cover" style={{ borderColor: C.line }} />)}
          </div>
        </div>
      )}

      {(sop.videos || []).length > 0 && (
        <div className="mt-4" data-testid="sop-videos">
          <div className="text-[12px] font-semibold uppercase tracking-wide mb-2 flex items-center gap-1" style={{ color: `${C.navy}B3` }}><Video size={13} /> Videos</div>
          <div className="flex flex-col gap-1">
            {sop.videos.map((v, i) => <a key={i} href={v} target="_blank" rel="noreferrer" className="text-[13px]" style={{ color: C.sky }}>{v}</a>)}
          </div>
        </div>
      )}

      {sop.next_steps && (
        <div className="mt-4 rounded-lg p-3 flex items-start gap-2" style={{ background: "rgba(47,125,90,0.08)" }} data-testid="sop-next-steps">
          <ArrowRight size={16} style={{ color: C.green }} className="mt-0.5 shrink-0" />
          <div><div className="text-[12px] font-semibold" style={{ color: C.green }}>What to do next</div>
            <div className="text-[13.5px]" style={{ color: C.charcoal }}>{sop.next_steps}</div></div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px]" style={{ borderColor: C.line, color: C.slate }}>
        <AudienceChips audiences={sop.audiences} />
        {sop.approved_by_name && <span>Approved by {sop.approved_by_name}</span>}
        {sop.review_due_at && <span>Review due {fmtDate(sop.review_due_at)}</span>}
        <span>Created by {sop.created_by_name}</span>
      </div>
    </div>
  );
}
